import React, { useState, useEffect, useRef } from 'react';
import './ShipNavbar.css';
import Ship from '../Ship/Ship';
import './VesselProfile.css';
import { useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import 'firebase/compat/analytics';
import 'firebase/compat/auth';
import snoozeIcon from './snooze.png';
import cleanIcon from './clean.png';
import gitIcon from './git.png';
import groom from './groom_suggestions.png';
import trigger from './email_trigger.png';

function ShipsNavbar({ lat, lon }) {
  const [isOpen, setIsOpen] = useState(false);
  const [ships, setShips] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const shipsPerPage = 12;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedShip, setSelectedShip] = useState(null);
  const [suggestedShips, setSuggestedShips] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchShips = async () => {
      try {
        const response = await fetch('https://backend-nu-two.vercel.app/getAllShips');
        if (response.ok) {
          const data = await response.json();
          const uniqueShipNames = [...new Set(data.map(ship => ship.SHIPNAME))];
          uniqueShipNames.sort((a, b) => a.localeCompare(b));
          setShips(uniqueShipNames);
        } else {
          console.error('Error fetching ships');
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };
    fetchShips();
  }, []);

  const handleSearch = async () => {
    if (searchTerm.trim() !== '') {
      const suggestions = await fetchShipSuggestions(searchTerm);
      if (suggestions.length > 0) {
        fetchShipDetails(suggestions[0]);
      }
    }
  };

  const fetchShipSuggestions = async (term) => {
    try {
      const response = await fetch(`https://backend-nu-two.vercel.app/searchShip/${encodeURIComponent(term)}`);
      if (response.ok) {
        const suggestions = await response.json();
        setSuggestedShips(suggestions.slice(0, 5)); // Limit suggestions to the top 5
        return suggestions;
      } else {
        console.error('Error fetching ship suggestions:', response.statusText);
        setSuggestedShips([]); // Clear suggestions if there's an error
        return [];
      }
    } catch (error) {
      console.error('Error:', error);
      setSuggestedShips([]); // Clear suggestions if there's an error
      return [];
    }
  };

  const fetchShipDetails = async (shipName) => {
    try {
      const uppercaseShipName = shipName.toUpperCase();
      const response = await fetch(`https://backend-nu-two.vercel.app/getShipInfo/${uppercaseShipName}`);
      if (response.ok) {
        const shipDetails = await response.json();
        setSelectedShip(shipDetails);

        const imo = shipDetails.IMO;
        const shipImageResponse = await fetch(`https://backend-nu-two.vercel.app/getShipImage/${imo}`);
        if (shipImageResponse.ok) {
          const shipImageData = await shipImageResponse.blob();
          const shipImageUrl = URL.createObjectURL(shipImageData);
          setSelectedShip(prevState => ({ ...prevState, shipImage: shipImageUrl }));
        } else {
          console.error('Error fetching ship image');
        }

        setIsModalOpen(true);
        setSuggestedShips([]); // Close the suggestions box after a ship is selected
        setSearchTerm('');
      } else {
        console.error('Error fetching ship details');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    setIsModalOpen(false);
    setSuggestedShips([]);
  };

  const handleSearchChange = async (event) => {
    const term = event.target.value;
    setSearchTerm(term);

    if (term.trim() !== '') {
      fetchShipSuggestions(term);
    } else {
      setSuggestedShips([]);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const filteredShips = ships.filter(ship => {
    return ship.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const onLocateShipOnMap = (shipName) => {
    navigate(`/mapdata?ship=${encodeURIComponent(shipName)}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleCheckOnMap = async() => {
    if (selectedShip && selectedShip.IMO) {
      const imo = selectedShip.IMO;
      try {
        // Fetch the biofouling report HTML using the backend API endpoint
        const response = await fetch(`https://backend-nu-two.vercel.app/getVesselRoute/${imo}`);
        if (response.ok) {
          const htmlContent = await response.text();
          
          // Create a new Blob object using the HTML content
          const blob = new Blob([htmlContent], { type: 'text/html' });
          
          // Create a URL for the Blob and open it in a new tab
          const imageUrl = URL.createObjectURL(blob);
          window.open(imageUrl, '_blank');
        } else {
          console.error('Failed to fetch report HTML');
        }
      } catch (error) {
        console.error('Error fetching report HTML:', error);
      }
    } else {
      console.error('Selected ship or IMO not available');
    }
  };    

  const handletco = async() => {
    if (selectedShip && selectedShip.IMO) {
      const imo = selectedShip.IMO;
      try {
        // Fetch the biofouling report PDF using the backend API endpoint
        const response = await fetch(`https://backend-nu-two.vercel.app/getTcoReport/${imo}`);
        if (response.ok) {
          const blob = await response.blob();
          
          // Ensure the fetched content is of PDF type
          if (blob.type === 'application/pdf') {
            const pdfUrl = URL.createObjectURL(blob);
            
            // Open the PDF URL in a new tab
            window.open(pdfUrl, '_blank');
          } else {
            console.error('Fetched content is not a PDF');
          }
        } else {
          console.error('Failed to fetch report PDF');
        }
      } catch (error) {
        console.error('Error fetching report PDF:', error);
      }
    } else {
      console.error('Selected ship or IMO not available');
    }
  };
  

  const handleGenerateReport = async () => {
    // Increment the count of button clicks
    logGenerateReportEvent();
  
    if (selectedShip && selectedShip.IMO) {
      const imo = selectedShip.IMO;
      try {
        // Fetch the biofouling report image using the backend API endpoint
        const response = await fetch(`https://backend-nu-two.vercel.app/getBFMImage/${imo}`);
        if (response.ok) {
          const blob = await response.blob();
          const imageUrl = URL.createObjectURL(blob);
  
          // Open the image URL in a new tab
          window.open(imageUrl, '_blank');
        } else {
          console.error('Failed to fetch report image');
        }
      } catch (error) {
        console.error('Error fetching report image:', error);
      }
    } else {
      console.error('Selected ship or IMO not available');
    }
  };  

  const handleCleanings = async () => {
    // Increment the count of button clicks
    logGenerateReportEvent();
  
    if (selectedShip && selectedShip.IMO) {
      const imo = selectedShip.IMO;
      try {
        // Fetch the biofouling report image using the backend API endpoint
        const response = await fetch(`https://backend-nu-two.vercel.app/getCleaning/${imo}`);
        if (response.ok) {
          const blob = await response.blob();
          const imageUrl = URL.createObjectURL(blob);
  
          // Open the image URL in a new tab
          window.open(imageUrl, '_blank');
        } else {
          console.error('Failed to fetch report image');
        }
      } catch (error) {
        console.error('Error fetching report image:', error);
      }
    } else {
      console.error('Selected ship or IMO not available');
    }
  };  

  const handleSpeedReport = async () => {
    // Increment the count of button clicks
    logGenerateReportEvent();
  
    if (selectedShip && selectedShip.IMO) {
      const imo = selectedShip.IMO;
      try {
        // Fetch the biofouling report image using the backend API endpoint
        const response = await fetch(`https://backend-nu-two.vercel.app/getSpeed/${imo}`);
        if (response.ok) {
          const blob = await response.blob();
          const imageUrl = URL.createObjectURL(blob);
  
          // Open the image URL in a new tab
          window.open(imageUrl, '_blank');
        } else {
          console.error('Failed to fetch report image');
        }
      } catch (error) {
        console.error('Error fetching report image:', error);
      }
    } else {
      console.error('Selected ship or IMO not available');
    }
  };  
  
  const logGenerateReportEvent = () => {
    const user = firebase.auth().currentUser;
    if (user) {
      // Get the unique user ID
      const userId = user.uid;
  
      // Log the custom event with the user's ID
      firebase.analytics().logEvent('generate_bfm_report', {
        userId: userId,
      });
    }
  };

  const handleClickOutside = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) &&
      !event.target.classList.contains('search-box') &&
      !event.target.classList.contains('suggestion')
    ) {
      setIsOpen(false);
    }
  };
  
  const handleDropdownClick = (event) => {
    event.stopPropagation();
  };  

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const pageCount = Math.ceil(ships.length / shipsPerPage);
  const visibleShips = ships.slice((currentPage - 1) * shipsPerPage, currentPage * shipsPerPage);
  const capitalizeWords = (str) => {
    return str.split(/([ ,])/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('');
  };
  
  return (
    <div className="ships-navbar-container">
      <div className="search-container">
        <input
          type="text"
          placeholder="Search ships..."
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          className="search-box"
        />
        <button className="search-btn" onClick={handleSearch}>Search</button>
      </div>
      {Boolean(suggestedShips.length) && (
        <div className="suggestions-container">
          <ul className="suggestions-list">
            {suggestedShips.map((shipName, index) => (
              <li key={index} className="suggestion" onClick={() => fetchShipDetails(shipName)}>
                {shipName}
              </li>
            ))}
          </ul>
        </div>
      )}
  
  <div className='space'>
  <button onClick={toggleDropdown} className="dropbtn">
    OUR SHIPS
  </button>
</div>
{isOpen && (
 <div
 className="dropdown-content"
 ref={dropdownRef}
 onClick={handleDropdownClick}
>
 <div className="scrollable-list">
   {visibleShips.map((shipName, index) => (
     <div
       key={index}
       onClick={() => {
         fetchShipDetails(shipName);
         setIsOpen(false);
       }}
     >
       {shipName}
     </div>
   ))}
 </div>
 {pageCount > 1 && (
   <div className="pagination">
     {currentPage > 3 && (
       <span onClick={() => handlePageChange(1)}>1</span>
     )}
     {currentPage > 4 && <span>...</span>}

     {Array.from(
       { length: Math.min(5, pageCount) },
       (_, i) => {
         const page =
           currentPage <= 3 ? i + 1 : currentPage - 2 + i;
         if (page > pageCount) return null;
         return (
           <span
             key={page}
             className={currentPage === page ? "active" : ""}
             onClick={() => handlePageChange(page)}
           >
             {page}
           </span>
         );
       }
     )}

     {currentPage < pageCount - 3 && <span>...</span>}
     {currentPage < pageCount - 2 && (
       <span onClick={() => handlePageChange(pageCount - 1)}>
         {pageCount - 1}
       </span>
     )}
     {currentPage < pageCount - 1 && (
       <span onClick={() => handlePageChange(pageCount)}>
         {pageCount}
       </span>
     )}

     <select
       value={currentPage}
       onChange={(e) =>
         handlePageChange(Number(e.target.value))
       }
       style={{ width: "120px" }} // Adjust dropdown width
     >
       {Array.from({ length: pageCount }, (_, i) => (
         <option key={i + 1} value={i + 1}>
           Page {i + 1}
         </option>
       ))}
     </select>
   </div>
 )}
</div>
)}

      {isModalOpen && selectedShip && (
        <div className="ship-modal">
          <div className="ship-name">
            {selectedShip.SHIPNAME}
          </div>
          <div className="ship-image-container">
            {selectedShip.shipImage && (
              <img
                src={selectedShip.shipImage}
                alt={selectedShip.SHIPNAME}
                className="ship-image"
              />
            )}
          </div>
          <div className="detail-container">
            <div className="detail-card">
              <div className="detail-header">IMO</div>
              <div className="detail-content">{selectedShip.IMO}</div>
            </div>
            <div className="detail-card">
              <div className="detail-header">Type</div>
              <div className="detail-content">{selectedShip.AIS_TYPE_SUMMARY}</div>
            </div>
            <div className="detail-card">
              <div className="detail-header">DD Date: </div>
              <div className="detail-content">{selectedShip["DD Date"]} </div>
            </div>
            <div className="detail-card">
              <div className="detail-header">Product Applied</div>
              <div className="detail-content">{capitalizeWords(selectedShip.Product)}</div>
            </div>
            <div className="detail-card temperature-card">
              <div className="temperature-icon"></div>
              <div className="detail-header">Temperature</div>
              <div className="detail-content">{parseFloat(selectedShip.TEMPERATURE).toFixed(1)} °C</div>
            </div>
            <div className="detail-card salinity-card">
              <div className="salinity-icon"></div>
              <div className="detail-header">Salinity</div>
              <div className="detail-content">{parseFloat(selectedShip.SALINITY).toFixed(1)}</div>
            </div>
            <div className="detail-card speed-card">
              <div className="speed-icon"></div>
              <div className="detail-header">Speed</div>
              <div className="detail-content">{selectedShip.SPEED > 100 ? (selectedShip.SPEED / 10).toFixed(2) : selectedShip.SPEED} knots</div>
            </div>
            <div className="detail-card last-port-card">
              <div className="last-port-icon"></div>
              <div className="detail-header">Last Port</div>
              <div className="detail-content">{selectedShip.LAST_PORT}</div>
            </div>
            <div className="detail-card last-port-card">
              <div className="last-port-icon"></div>
              <div className="detail-header">Next Port</div>
              <div className="detail-content">{selectedShip.NEXT_PORT_NAME}</div>
            </div>
            <div className="detail-card">
              <div className="detail-header">Average Speed</div>
              <div className="detail-content">{selectedShip.AVG_SPEED} knots</div>
            </div>
            <div className="detail-card">
              <div className="detail-header">Max Speed</div>
              <div className="detail-content">{selectedShip.MAX_SPEED} knots</div>
            </div>
            <div className="detail-card distance-card">
              <div className="distance-icon"></div>
              <div className="detail-header">Distance Travelled Between Last Port and Next Port</div>
              <div className="detail-content">{selectedShip.DISTANCE_TRAVELLED} km</div>
            </div>
          </div>
          <div className="modal-button">
          <button className="close-modal-btn" onClick={() => setIsModalOpen(false)}>Close</button>
  <button className="check-on-map-btn" onClick={handleCheckOnMap}>Vessel Route</button>
 
  <button className="generate-report-btn" onClick={handleGenerateReport}>BFM Report</button>
  
  <button className="generate-tco-btn" onClick={handletco}>TCO Report</button>
  <button className="check-clean-btn" onClick={handleCleanings} style={{ backgroundColor: '#007bff', color: '#fff', padding: '10px 20px', border: 'none', borderRadius: '5px', fontSize: '16px' }}>
    Cleanings <span style={{ fontSize: '0.7em', color: 'yellow', marginLeft: '5px' }}>(Beta)</span>
  </button>
  <button className="check-clean-btn" onClick={handleCleanings} style={{ backgroundColor: '#007bff', color: '#fff', padding: '10px 20px', border: 'none', borderRadius: '5px', fontSize: '16px' }}>
    Cleaning Route <span style={{ fontSize: '0.7em', color: 'yellow', marginLeft: '5px' }}>(Beta)</span>
  </button>
  <button className="generate-sl-btn" onClick={handleSpeedReport}>Speed/Loss Report</button>
</div>

        </div>
      )}
    </div>
  );
}

function VesselProfile() {
  const redirectToMarketProfile = () => {
    window.location.href = 'https://market-fleet.vercel.app/';
  };

  return (
    <div className="vessel-profile-container">
      <div className="content-container">
        <ShipsNavbar />
      </div>
      <div className="button-container1">
        <button className="market-profile-button" onClick={redirectToMarketProfile}>Market Profile</button>
      </div>
      <div className="cleaning-form-container">
  <span className="cleaning-form-text">Our Other Services</span>
  <div className="icon-stack">
    <img src={cleanIcon} alt="Clean Icon" className="circular-icon" 
    onClick={() => window.open('https://main.d1m5kqva07sr5y.amplifyapp.com/dashboard', '_blank')}   />
    <div className="fleet-management-text">Historic Data Puller</div>
    {/* <img src={gitIcon} alt="Clean Icon" className="circular-icon" 
    onClick={() => window.open('https://gitcoatings.com/', '_blank')}   /> */}
    {/* Company Website */}
    <img src={groom} alt="Clean Icon" className="circular-icon" 
    onClick={() => window.open('https://groom-chart.vercel.app/groom', '_blank')}   />
    Grooming Suggestion
    <img src={"https://gitcoatings.com/wp-content/uploads/2024/01/cropped-GIT_coatings_logomark_main-dark_RGB-32x32.png"} alt="Clean Icon" className="circular-icon" 
    onClick={() => window.open('https://routes-alpha-topaz.vercel.app/home', '_blank')}   />
    <div className="fleet-management-text">Vessel Routes</div>
    <img src={trigger} alt="Clean Icon" className="circular-icon" 
    onClick={() => window.open('https://email-trigger-sigma.vercel.app/login', '_blank')}   />
    <div className="fleet-management-text">Email Triggers</div>
    <img src={"https://play-lh.googleusercontent.com/6Z-X8HxCHUz9nECa3GNJeL-QjSO5IhqWoAVE_ZEo8yLSrVmCecu5bwM6MwvBcWx4148"} alt="Clean Icon" className="circular-icon" 
    onClick={() => window.open('https://marrinetraffic-dash.vercel.app/dashboard', '_blank')}   />
     <div className="fleet-management-text">Marrinetraffic Fleet Management</div>
  </div>
</div>
    </div>
  );
}


export default VesselProfile;